import React from "react";
import Highlighter from "react-highlight-words";
import { makeStyles } from "tss-react/mui";

import { Box, Typography } from "@mui/material";
import { Tooltip } from "@shared/Tooltip";
import { CellContext } from "@tanstack/react-table";

import { GroupOverviewData } from "../../GroupPage.utils";

const useStyles = makeStyles()((theme) => ({
    box: { display: "flex", alignItems: "center" },
    cellContent: {
        display: "inline-block",
        maxWidth: 240,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        position: "relative",
    },
    highlightedText: {
        backgroundColor: theme.palette.emteria.warning,
    },
}));

type Props = CellContext<GroupOverviewData, unknown> & { withIndent?: boolean };

const TableCell = (props: React.PropsWithChildren<Props>) => {
    const [isTruncated, setIsTruncated] = React.useState(false);
    const textRef = React.useRef<HTMLSpanElement>(null);

    const { classes } = useStyles();

    const value = props.getValue<string>();
    const globalFilter: string = props.table.getState().globalFilter;

    React.useEffect(() => {
        const el = textRef.current;
        if (el) {
            setIsTruncated(el.scrollWidth > el.clientWidth);
        }
    }, [value]);

    return (
        <Tooltip title={isTruncated ? value : ""} placement="top" PopperProps={{ disablePortal: false }}>
            <Box className={classes.box} style={{ paddingLeft: props.withIndent ? `${props.row.depth * 2}rem` : "unset" }}>
                {props.children}
                <Typography variant="body1" component="span" className={classes.cellContent} ref={textRef}>
                    {/* autoEscape: the search term is whatever the user typed, so "(" or "[" must not reach the matcher as syntax. */}
                    <Highlighter
                        searchWords={[globalFilter]}
                        textToHighlight={value ?? ""}
                        autoEscape
                        highlightClassName={classes.highlightedText}
                    />
                </Typography>
            </Box>
        </Tooltip>
    );
};

export default TableCell;
